import React, { useState } from 'react';
import { Modal } from '../components/design-system/Modal';
import { logError } from '../utils/logger';

const walletCards = {
  csr: {
    name: 'Chase Sapphire Reserve',
    image: '/cards/chase_sapphire_reserve_credit_card.png',
  },
  amexGold: {
    name: 'American Express Gold',
    image: '/cards/american_express_gold_card.png',
  },
  inkPreferred: {
    name: 'Ink Business Preferred',
    image: '/cards/ink_business_preferred_credit_card.png',
  },
};

const CardChip: React.FC<{ name: string; image: string }> = ({
  name,
  image,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      verticalAlign: 'middle',
    }}
  >
    <img
      src={image}
      alt={name}
      style={{
        width: 28,
        height: 18,
        objectFit: 'cover',
        borderRadius: 3,
        verticalAlign: 'middle',
      }}
    />
    <span style={{ fontWeight: 600 }}>{name}</span>
  </span>
);

const scenarios = [
  {
    title: 'Apple Wallet',
    card: walletCards.csr,
    textBefore: 'At dinner, CardClues opens ',
    textAfter: ' in Apple Pay for 3x points',
  },
  {
    title: 'Apple Watch',
    card: walletCards.inkPreferred,
    textBefore: 'On transit, CardClues opens ',
    textAfter: ' on your Apple Watch for 3x points',
  },
  {
    title: 'Track credits',
    card: walletCards.amexGold,
    textBefore: 'CardClues reminds you that your ',
    textAfter: ' monthly Grubhub credit is expiring, saving $10',
  },
];

const AppSplashPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const wantsBeta = true;
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(0);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');

    fetch('https://smart-wallet-feedback-service.fly.dev/addEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, wantsBeta }),
    })
      .then(() => {
        setIsSubmitSuccessful(true);
        setEmail('');
      })
      .catch((error) => {
        logError('Error submitting waitlist form:', error);
      });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (hasAttemptedSubmit) {
      if (!newEmail.trim()) {
        setEmailError('Email is required');
      } else if (!validateEmail(newEmail)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
  };

  const accentBlue = '#86A1B3';

  const BigHeader: React.FC<{
    children: React.ReactNode;
    color?: string;
    style?: React.CSSProperties;
    as?: 'h1' | 'h2' | 'p';
  }> = ({ children, color = '#000', style, as: Tag = 'p' }) => (
    <Tag
      style={{
        fontFamily: 'Poppins',
        fontWeight: 700,
        fontSize: '3.3rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        color,
        margin: 0,
        ...style,
      }}
    >
      {children}
    </Tag>
  );

  return (
    <div style={{ backgroundColor: '#eef1f4', minHeight: '100vh' }}>
      {/* ── Section 1: Hero ── */}
      <div
        style={{
          background: 'linear-gradient(to bottom, #fff 0%, #eef1f4 66%)',
          overflow: 'hidden',
        }}
      >
        <section
          className="hero-section"
          style={{
            maxWidth: 1000,
            margin: '0 auto',
            padding: '60px 24px 40px',
          }}
        >
          {/* Logo */}
          <a
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: '#3F79A4',
              marginBottom: 48,
            }}
          >
            <h3 style={{ margin: 0, fontFamily: 'Poppins', fontWeight: 700 }}>
              CardClues
            </h3>
          </a>

          <div
            className="hero-columns"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 40,
            }}
          >
            {/* Left column */}
            <div
              className="hero-left"
              style={{ flex: '1 1 55%', paddingTop: 16 }}
            >
              <BigHeader as="h1" style={{ marginBottom: 16 }}>
                Your credit card rewards, automated.
              </BigHeader>
              <p
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '1.08rem',
                  color: '#000',
                  lineHeight: 1.6,
                  margin: '0 0 10px',
                  maxWidth: 600,
                }}
              >
                CardClues <strong>opens your best card in Apple Pay</strong> and{' '}
                <strong>tells you what card to pull out, when</strong>.
              </p>
              <p
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '1.08rem',
                  color: '#000',
                  lineHeight: 1.6,
                  margin: '0 0 28px',
                  maxWidth: 600,
                }}
              >
                Track rewards, credits, and more.
              </p>

              <p
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  color: '#000',
                  margin: '0 0 12px',
                }}
              >
                Get beta access now
              </p>

              {/* Email form */}
              <form onSubmit={handleSubmit} style={{ maxWidth: 420 }}>
                <div style={{ display: 'flex', position: 'relative' }}>
                  <input
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      fontSize: '1.2rem',
                      borderRadius: 9999,
                      border: `2px solid ${
                        hasAttemptedSubmit && emailError ? '#ff3b30' : '#000'
                      }`,
                      fontFamily: 'Poppins',
                      backgroundColor: '#fff',
                      width: '100%',
                    }}
                    placeholder="you@example.com"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  <button
                    type="submit"
                    style={{
                      position: 'absolute',
                      color: '#fff',
                      fontWeight: 600,
                      borderRadius: 9999,
                      right: 4,
                      top: 4,
                      bottom: 4,
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: '#000',
                      fontFamily: 'Poppins',
                      padding: '0 24px',
                      height: 'calc(100% - 8px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.08rem',
                    }}
                  >
                    Submit
                  </button>
                </div>
                {hasAttemptedSubmit && emailError && (
                  <p
                    style={{
                      color: '#ff3b30',
                      fontSize: '1.05rem',
                      marginTop: 6,
                      fontFamily: 'Poppins',
                    }}
                  >
                    {emailError}
                  </p>
                )}
                {isSubmitSuccessful && (
                  <p
                    style={{
                      color: '#4CAF50',
                      fontSize: '1.05rem',
                      marginTop: 6,
                      fontFamily: 'Poppins',
                    }}
                  >
                    Thanks! We'll notify you when we launch.
                  </p>
                )}
              </form>

              <p
                style={{
                  fontSize: '0.96rem',
                  color: accentBlue,
                  marginTop: 8,
                  fontFamily: 'Poppins',
                }}
              >
                By submitting your email, you agree to our{' '}
                <button
                  type="button"
                  style={{
                    background: 'none',
                    fontSize: '0.96rem',
                    color: accentBlue,
                    textDecoration: 'underline',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontFamily: 'Poppins',
                  }}
                  onClick={() => setIsModalOpen(true)}
                >
                  Privacy Policy
                </button>
              </p>
            </div>

            {/* Right column — feature image */}
            <div
              className="hero-right"
              style={{
                flex: '1 1 55%',
                marginRight: -100,
                marginLeft: -60,
              }}
            >
              <img
                src="/feature-hero.webp"
                alt="CardClues app showing categories and Apple Wallet"
                className="hero-phone-img"
                style={{
                  width: '145%',
                  maxWidth: 780,
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          </div>
        </section>
      </div>

      {/* ── Section 2: Works with any way you pay (simplified) ── */}
      <section
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          padding: '48px 24px',
        }}
      >
        <BigHeader as="h2" style={{ marginBottom: 32 }}>
          Works with any way you pay
        </BigHeader>

        <div
          className="feature-cards-row"
          style={{
            display: 'flex',
            gap: 16,
          }}
        >
          {[
            {
              title: 'Apple Wallet',
              desc: 'Opens your best card in Apple Pay automatically based on where you are.',
            },
            {
              title: 'Apple Watch',
              desc: 'Works on your wrist too — the right card, ready to tap.',
            },
            {
              title: 'Track credits',
              desc: 'Never miss a monthly credit. Get reminders before they expire.',
            },
          ].map((card) => (
            <div
              key={card.title}
              style={{
                flex: 1,
                backgroundColor: '#fff',
                borderRadius: 16,
                padding: '28px 24px',
              }}
            >
              <p
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#000',
                  margin: '0 0 8px',
                  letterSpacing: '-0.01em',
                }}
              >
                {card.title}
              </p>
              <p
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '1.02rem',
                  color: '#86A1B3',
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {card.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section 2 (OLD): Works with any way you pay — commented out ──
      <section
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          padding: '48px 24px',
        }}
      >
        <BigHeader as="h2" style={{ marginBottom: 16 }}>
          Works with any way you pay
        </BigHeader>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 24,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: 'Poppins-Bold',
              fontSize: '1.02rem',
              color: '#000',
            }}
          >
            Based on
          </span>
          {Object.values(walletCards).map((card) => (
            <img
              key={card.name}
              src={card.image}
              alt={card.name}
              style={{
                width: 64,
                height: 40,
                objectFit: 'cover',
                borderRadius: 6,
              }}
            />
          ))}
        </div>

        <div
          className="scenario-layout"
          style={{
            display: 'flex',
            gap: 24,
            alignItems: 'stretch',
          }}
        >
          <div
            className="scenario-cards"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {scenarios.map((card, i) => (
              <button
                key={card.title}
                type="button"
                onClick={() => setSelectedScenario(i)}
                style={{
                  backgroundColor: selectedScenario === i ? '#e8edf1' : '#fff',
                  borderRadius: 12,
                  padding: '16px 20px',
                  border:
                    selectedScenario === i
                      ? '2px solid #86A1B3'
                      : '2px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Poppins-Bold',
                    fontSize: '1.68rem',
                    color: '#000',
                    margin: '0 0 4px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {card.title}
                </p>
                <p
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: '1.02rem',
                    color: '#86A1B3',
                    margin: 0,
                    lineHeight: 2,
                    fontWeight: 400,
                  }}
                >
                  {card.textBefore}
                  <CardChip name={card.card.name} image={card.card.image} />
                  {card.textAfter}
                </p>
              </button>
            ))}
          </div>

          <div
            className="scenario-video"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
            }}
          >
            <img
              src="/iphone-feature.webp"
              alt="CardClues on iPhone"
              style={{
                maxHeight: 500,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </section>
      */}

      {/* ── Section 3: Why CardClues? ── */}
      <section
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          padding: '48px 24px',
        }}
      >
        <p
          style={{
            fontFamily: 'Poppins',
            fontWeight: 700,
            fontSize: '1.02rem',
            letterSpacing: '0.04em',
            color: '#000',
            margin: '0 0 24px',
          }}
        >
          Why CardClues?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            {
              bold: 'More secure',
              desc: 'No bank accounts required',
            },
            {
              bold: 'Built for iOS',
              desc: 'uses official iOS Apple Wallet shortcut actions',
            },
            {
              bold: 'Supports 200+ cards',
              desc: 'with up-to-date rewards information',
            },
          ].map((prop) => (
            <p
              key={prop.bold}
              style={{
                fontFamily: 'Poppins',
                fontSize: '1.32rem',
                color: '#666',
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              <span style={{ fontWeight: 700, color: '#000' }}>
                {prop.bold}
              </span>
              {' - '}
              {prop.desc}
            </p>
          ))}
        </div>
      </section>

      {/* ── Section 4: Footer ── */}
      <footer
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          padding: '48px 24px 32px',
        }}
      >
        <p
          style={{
            fontFamily: 'Poppins',
            fontWeight: 700,
            fontSize: '7.8rem',
            color: '#000',
            letterSpacing: '-0.02em',
            margin: '0 0 8px',
          }}
        >
          CardClues
        </p>
        <p
          style={{
            fontFamily: 'Poppins',
            fontSize: '0.75rem',
            color: '#aaa',
            margin: '0 0 12px',
          }}
        >
          Copyright 2026 CardClues
        </p>
        <div style={{ display: 'flex', gap: 16 }}>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: accentBlue,
              fontSize: '0.9rem',
              fontFamily: 'Poppins',
            }}
          >
            Privacy policy
          </button>
          <a
            href="mailto:hello@cardclues.com"
            style={{
              color: accentBlue,
              fontSize: '0.9rem',
              fontFamily: 'Poppins',
              textDecoration: 'none',
            }}
          >
            Contact us
          </a>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={isModalOpen} handleClose={() => setIsModalOpen(false)}>
        <h3
          style={{
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
          }}
        >
          Privacy Policy
        </h3>
        <p style={{ marginBottom: '1rem' }}>
          This Privacy Policy describes how CardClues collects, uses, and
          discloses your personal information when you use our application and
          services.
        </p>
        <p style={{ marginBottom: '1rem' }}>
          We collect information you provide directly to us, such as when you
          create an account, update your profile, use interactive features,
          participate in contests, promotions, or surveys, request customer
          support or otherwise communicate with us.
        </p>
        <p style={{ marginBottom: '1rem' }}>
          We may use the information we collect to provide, maintain, and
          improve our services, develop new products and services, communicate
          with you, and personalize your experience.
        </p>
        <p style={{ marginBottom: '1rem' }}>
          We may share your information with third-party vendors and service
          providers who perform services on our behalf, such as payment
          processing, data analysis, email delivery, hosting services, and
          customer service.
        </p>
      </Modal>

      <style>
        {`
          input::placeholder {
            color: #86A1B3;
            font-family: 'Poppins';
          }

          @media (max-width: 768px) {
            .hero-columns {
              flex-direction: column !important;
            }
            .hero-left {
              flex: 1 1 100% !important;
            }
            .hero-right {
              flex: 1 1 100% !important;
              justify-content: center;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            .hero-phone-img {
              width: 80% !important;
              max-width: 400px !important;
            }
            .feature-cards-row {
              flex-direction: column !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AppSplashPage;
